<template>
  <div>
    <!-- 1. Leader info text -->
    <v-expansion-panels class="mb-4" flat>
      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold">
          Mitä pidetään työn arvona?
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <p>
            Vastikkeetta tehdyn työn arvona pidetään 20 euroa tunnilta työn
            tekijää kohden. Henkilön vastikkeetta tehdyn työn arvoon sisältyy
            myös tavanomaisten pienkoneiden käyttö. Jos vastikkeetta tehty työ
            tehdään traktorilla tai muulla vastaavalla työkoneella esim.
            mönkijällä tai moottorikelkalla, työkoneen käyttämisen arvoksi
            luetaan lisäksi 40 euroa käyttötunnilta.
          </p>
          <p class="font-weight-bold mt-2">
            Missä hankkeissa vastikkeetta hankkeen hyväksi tehtävä työ voidaan
            hyväksyä kustannukseksi?
          </p>
          <p>
            Vastikkeeton työ voidaan hyväksyä kustannukseksi yleishyödyllisissä
            investoinnissa ja kehittämishankkeessa sekä yhteisöllisissä
            tietoliikenneverkko- ja vesihuoltoinvestoinneissa.
          </p>
          <p class="font-weight-bold mt-2">
            Kuinka paljon yksityisestä rahoituksesta voi olla vastikkeetta
            hankkeen hyväksi tehtävää työtä?
          </p>
          <p>
            Yksityisestä rahoituksesta enintään 100 prosenttia voi olla
            vastikkeetta tehtyä työtä.
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- 2. Work task settings (superadmin only) -->
    <v-card v-if="$isSuperAdmin" class="mb-4">
      <v-card-title>
        Työtehtäväasetukset
        <v-spacer />
        <v-btn color="primary" small @click="openTaskDialog()">
          <v-icon left small>mdi-plus</v-icon>
          Lisää tehtävä
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="taskHeaders"
        :items="workTasks"
        :items-per-page="-1"
        hide-default-footer
        dense
      >
        <template v-slot:item.active="{ item }">
          <v-icon :color="item.active ? 'green' : 'grey'">
            {{ item.active ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>
        <template v-slot:item.minPersonHours="{ item }">
          {{ item.minPersonHours != null ? item.minPersonHours : "Ei rajaa" }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="openTaskDialog(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small @click="deactivateTask(item)">
            <v-icon small>{{ item.active ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Task edit dialog -->
      <v-dialog v-model="taskDialog" max-width="500px" persistent>
        <v-card>
          <v-card-title>{{
            editingTask.id ? "Muokkaa tehtävää" : "Uusi tehtävä"
          }}</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="editingTask.title"
              label="Tehtävän nimi"
              outlined
              dense
              :rules="[$rules.required]"
            />
            <v-text-field
              v-model.number="editingTask.minPersonHours"
              label="Minimi henkilötyötunnit (tyhjä = ei rajaa)"
              outlined
              dense
              type="number"
              min="0"
              clearable
            />
            <v-text-field
              v-model="editingTask.ajankohta"
              label="Työaika (esim. 5/26)"
              outlined
              dense
            />
            <v-switch v-model="editingTask.active" label="Aktiivinen" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="taskDialog = false">Peruuta</v-btn>
            <v-btn
              color="primary"
              :disabled="!editingTask.title"
              @click="saveTask"
              >Tallenna</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>

    <!-- 3. Hour entry card (user only) -->
    <template v-if="$isUser || $isSuperAdmin">
      <v-card class="mb-4">
        <v-card-title>
          Savusauna — tuntimerkinnät
          <v-spacer />
          <v-btn color="primary" @click="openHourDialog()">
            <v-icon left>mdi-plus</v-icon>
            Uusi tuntimerkintä
          </v-btn>
        </v-card-title>

        <!-- Task hour summary with warnings -->
        <v-card-text v-if="taskHourSummary.length">
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Työtehtävä</th>
                <th>Henkilötyötunnit yhteensä</th>
                <th>Minimi</th>
                <th>Tilanne</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="s in taskHourSummary"
                :key="s.taskId"
                :class="{ 'orange--text': s.belowMin }"
              >
                <td>{{ s.title }}</td>
                <td>{{ s.totalPersonHours }}</td>
                <td>
                  {{ s.minPersonHours != null ? s.minPersonHours : "Ei rajaa" }}
                </td>
                <td>
                  <v-icon v-if="s.belowMin" color="orange" small
                    >mdi-alert</v-icon
                  >
                  {{ s.belowMin ? "Alle minimin" : "OK" }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-data-table
          :headers="hourHeaders"
          :items="activeHours"
          :items-per-page="-1"
          hide-default-footer
          sort-by="date"
          :sort-desc="true"
          dense
        >
          <template v-slot:item.date="{ item }">
            {{ $formatDate(item.date) }}
          </template>
          <template v-slot:item.worker="{ item }">
            {{ item.worker ? item.worker.name : "" }}
          </template>
          <template v-slot:item.createdBy="{ item }">
            {{ item.createdBy ? item.createdBy.user : "" }}
          </template>
          <template v-slot:item.taskTitle="{ item }">
            <span
              :class="{
                'orange--text font-weight-bold': isTaskBelowMin(item.taskId),
              }"
            >
              {{ item.taskTitle }}
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <template v-if="canEditItem(item)">
              <v-btn icon small @click="openHourDialog(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="deleteHour(item)">
                <v-icon small color="red">mdi-delete</v-icon>
              </v-btn>
            </template>
          </template>
        </v-data-table>
      </v-card>

      <!-- Hour entry/edit dialog -->
      <v-dialog
        v-model="hourDialog"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        max-width="600px"
        persistent
      >
        <v-card>
          <v-card-title>{{
            editingHour.id ? "Muokkaa merkintää" : "Uusi tuntimerkintä"
          }}</v-card-title>
          <v-card-text>
            <modal-picker
              v-model="editingHour.date"
              label="Päivämäärä"
              required
            />

            <v-select
              v-model="editingHour.taskId"
              :items="activeWorkTasks"
              item-text="title"
              item-value="id"
              label="Työtehtävä"
              outlined
              dense
              :rules="[$rules.required]"
              class="mt-3"
            />

            <v-textarea
              v-model="editingHour.description"
              label="Tarkempi kuvaus (valinnainen)"
              outlined
              dense
              rows="2"
              auto-grow
            />

            <v-text-field
              v-model.number="editingHour.personHours"
              label="Henkilötyötunnit"
              outlined
              dense
              type="number"
              step="0.5"
              min="0"
            />

            <v-text-field
              v-model.number="editingHour.machineHours"
              label="Traktori- ja konetyötunnit"
              outlined
              dense
              type="number"
              step="0.5"
              min="0"
            />

            <!-- Multi-worker combobox for new entries -->
            <div v-if="!editingHour.id">
              <div class="d-flex align-center justify-space-between mt-2 mb-1">
                <span class="caption grey--text">Lisää työntekijöitä</span>
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  :disabled="myselfAlreadyAdded"
                  @click="addMyself"
                >
                  <v-icon left x-small>mdi-account-plus</v-icon>
                  Lisää minut
                </v-btn>
              </div>
              <v-combobox
                v-model="editingHour.workers"
                :items="henkilotItems"
                item-text="name"
                :return-object="true"
                multiple
                chips
                deletable-chips
                label="Työntekijät"
                outlined
                dense
                hint="Valitse henkilölistasta tai kirjoita vapaa nimi"
                persistent-hint
              />
            </div>

            <!-- Single worker display for editing existing entry -->
            <v-combobox
              v-else
              v-model="editingHour.singleWorker"
              :items="henkilotItems"
              item-text="name"
              :return-object="true"
              label="Työntekijä"
              outlined
              dense
            />

            <!-- Max hours warning -->
            <v-alert
              v-if="hourDialogWarning"
              type="warning"
              dense
              text
              class="mt-2"
            >
              {{ hourDialogWarning }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="hourDialog = false">Peruuta</v-btn>
            <v-btn
              v-if="!editingHour.id"
              outlined
              color="primary"
              :disabled="!canSaveHour"
              :loading="saving"
              @click="saveAndAddAnother"
            >
              Tallenna ja lisää toinen
            </v-btn>
            <v-btn
              color="primary"
              :disabled="!canSaveHour"
              :loading="saving"
              @click="saveHour"
            >
              {{ saveButtonText }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- 4. Alert for non-user -->
    <v-alert v-if="!$isUser && !$isSuperAdmin" type="info" class="mb-4">
      Sinulla ei ole oikeutta syöttää työtunteja.
    </v-alert>

    <!-- 5. Deleted hours view (superadmin only) -->
    <v-card v-if="$isSuperAdmin" class="mb-4">
      <v-card-title>
        Poistetut merkinnät
        <v-spacer />
        <v-switch
          v-model="showDeleted"
          label="Näytä poistetut"
          class="mt-0"
          hide-details
          dense
        />
      </v-card-title>
      <template v-if="showDeleted">
        <v-data-table
          :headers="deletedHourHeaders"
          :items="deletedHours"
          :items-per-page="-1"
          hide-default-footer
          dense
        >
          <template v-slot:item.date="{ item }">
            {{ $formatDate(item.date) }}
          </template>
          <template v-slot:item.worker="{ item }">
            {{ item.worker ? item.worker.name : "" }}
          </template>
          <template v-slot:item.deletedBy="{ item }">
            {{ item.deletedBy ? item.deletedBy.user : "" }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small color="primary" @click="restoreHour(item)">
              <v-icon left small>mdi-restore</v-icon>
              Palauta
            </v-btn>
          </template>
        </v-data-table>
      </template>
    </v-card>

    <!-- 6. Report section -->
    <v-card class="mb-4">
      <v-card-title>Raportti</v-card-title>
      <v-card-text>
        <v-row align="center" class="mb-2">
          <v-col cols="auto">
            <v-select
              v-model="reportYear"
              :items="availableYears"
              label="Vuosi"
              outlined
              dense
              hide-details
              style="max-width: 150px"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" class="mr-2" @click="copyReport">
              <v-icon left>mdi-content-copy</v-icon>
              Kopioi raportti
            </v-btn>
            <v-btn color="primary" @click="downloadXlsx">
              <v-icon left>mdi-file-excel</v-icon>
              Lataa XLSX
            </v-btn>
          </v-col>
        </v-row>

        <v-tabs v-model="reportTab" background-color="transparent">
          <v-tab>Yhteenveto</v-tab>
          <v-tab>Työtehtävittäin</v-tab>
          <v-tab>Työntekijöittäin</v-tab>
        </v-tabs>

        <v-tabs-items v-model="reportTab" touchless>
          <!-- Yhteenveto tab -->
          <v-tab-item>
            <v-simple-table dense class="mt-3">
              <thead>
                <tr>
                  <th>Työtehtävä</th>
                  <th>Henkilötyötunnit yhteensä</th>
                  <th>Konetunnit yhteensä</th>
                  <th>Minimi</th>
                  <th>Tilanne</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="s in reportTaskSummary"
                  :key="s.taskId"
                  :class="{ 'orange--text': s.belowMin }"
                >
                  <td>{{ s.title }}</td>
                  <td>{{ s.totalPersonHours }}</td>
                  <td>{{ s.totalMachineHours }}</td>
                  <td>
                    {{
                      s.minPersonHours != null ? s.minPersonHours : "Ei rajaa"
                    }}
                  </td>
                  <td>
                    <v-icon v-if="s.belowMin" color="orange" small
                      >mdi-alert</v-icon
                    >
                    {{ s.belowMin ? "Alle minimin" : "OK" }}
                  </td>
                </tr>
                <tr class="font-weight-bold">
                  <td>Yhteensä</td>
                  <td>
                    {{
                      reportTaskSummary.reduce(
                        (sum, r) => sum + r.totalPersonHours,
                        0,
                      )
                    }}
                  </td>
                  <td>
                    {{
                      reportTaskSummary.reduce(
                        (sum, r) => sum + r.totalMachineHours,
                        0,
                      )
                    }}
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-tab-item>

          <!-- Työtehtävittäin tab -->
          <v-tab-item>
            <v-data-table
              :headers="reportByTaskHeaders"
              :items="reportByTask"
              :items-per-page="-1"
              hide-default-footer
              show-expand
              item-key="taskId"
              dense
              class="mt-2"
            >
              <template v-slot:item.title="{ item }">
                <span
                  :class="{ 'orange--text font-weight-bold': item.belowMin }"
                >
                  {{ item.title }}
                </span>
              </template>
              <template v-slot:item.belowMin="{ item }">
                <v-icon v-if="item.belowMin" color="orange" small
                  >mdi-alert</v-icon
                >
                <span :class="{ 'orange--text': item.belowMin }">
                  {{ item.belowMin ? "Alle minimin" : "OK" }}
                </span>
              </template>
              <template v-slot:item.minPersonHours="{ item }">
                {{
                  item.minPersonHours != null ? item.minPersonHours : "Ei rajaa"
                }}
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-3">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Päivämäärä</th>
                        <th>Työntekijä</th>
                        <th>Henkilötyötunnit</th>
                        <th>Konetunnit</th>
                        <th>Kuvaus</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="e in item.entries" :key="e.id">
                        <td>{{ $formatDate(e.date) }}</td>
                        <td>{{ e.worker ? e.worker.name : "" }}</td>
                        <td>{{ e.personHours || 0 }}</td>
                        <td>{{ e.machineHours || 0 }}</td>
                        <td>{{ e.description }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </td>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Työntekijöittäin tab -->
          <v-tab-item>
            <v-data-table
              :headers="reportByWorkerHeaders"
              :items="reportByWorker"
              :items-per-page="-1"
              hide-default-footer
              show-expand
              item-key="workerName"
              dense
              class="mt-2"
            >
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-3">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Päivämäärä</th>
                        <th>Työtehtävä</th>
                        <th>Henkilötyötunnit</th>
                        <th>Konetunnit</th>
                        <th>Kuvaus</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="e in item.entries" :key="e.id">
                        <td>{{ $formatDate(e.date) }}</td>
                        <td>{{ e.taskTitle }}</td>
                        <td>{{ e.personHours || 0 }}</td>
                        <td>{{ e.machineHours || 0 }}</td>
                        <td>{{ e.description }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </td>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  addDoc,
  collection,
  doc,
  getDocs,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy as orderByFirestore,
  arrayUnion,
} from "firebase/firestore";
import { firestore as db } from "@/plugins/firebase.app";
import ModalPicker from "@/components/ModalPicker";
import dayjs from "dayjs";
import { orderBy } from "lodash";
import * as XLSX from "xlsx";

export default {
  name: "SavusaunaHours",
  components: { ModalPicker },

  data() {
    return {
      // Work tasks
      workTasks: [],
      taskDialog: false,
      editingTask: {
        id: null,
        title: "",
        minPersonHours: null,
        active: true,
        ajankohta: "",
      },

      // Hours
      hours: [],
      deletedHoursList: [],
      hourDialog: false,
      editingHour: {
        id: null,
        date: dayjs().format("YYYY-MM-DD"),
        taskId: null,
        description: "",
        personHours: null,
        machineHours: null,
        workers: [],
        singleWorker: null,
      },
      saving: false,

      // Deleted view
      showDeleted: false,

      // Report
      reportYear: dayjs().year(),
      reportTab: 0,
      lastTaskId: null,

      // Task table headers
      taskHeaders: [
        { text: "Työaika", value: "ajankohta", width: 80 },
        { text: "Tehtävä", value: "title" },
        { text: "Minimi h", value: "minPersonHours", width: 120 },
        { text: "Aktiivinen", value: "active", width: 100 },
        { text: "", value: "actions", sortable: false, width: 100 },
      ],

      // Hour table headers
      hourHeaders: [
        { text: "Päivämäärä", value: "date" },
        { text: "Työtehtävä", value: "taskTitle" },
        { text: "Kuvaus", value: "description" },
        { text: "Henkilötyötunnit", value: "personHours", width: 130 },
        { text: "Konetunnit", value: "machineHours", width: 110 },
        { text: "Työntekijä", value: "worker" },
        { text: "Kirjaaja", value: "createdBy" },
        { text: "", value: "actions", sortable: false, width: 100 },
      ],

      // Deleted hours table headers
      deletedHourHeaders: [
        { text: "Päivämäärä", value: "date" },
        { text: "Työtehtävä", value: "taskTitle" },
        { text: "Henkilötyötunnit", value: "personHours" },
        { text: "Konetunnit", value: "machineHours" },
        { text: "Työntekijä", value: "worker" },
        { text: "Poistanut", value: "deletedBy" },
        { text: "", value: "actions", sortable: false, width: 120 },
      ],

      // Report by-task table headers
      reportByTaskHeaders: [
        { text: "Työtehtävä", value: "title" },
        {
          text: "Henkilötyötunnit yht.",
          value: "totalPersonHours",
          width: 160,
        },
        { text: "Konetunnit yht.", value: "totalMachineHours", width: 140 },
        { text: "Minimi h", value: "minPersonHours", width: 120 },
        { text: "Tilanne", value: "belowMin", width: 110 },
        { text: "Työntekijöitä", value: "workerCount", width: 120 },
        { text: "Merkintöjä", value: "entryCount", width: 110 },
        { text: "", value: "data-table-expand", sortable: false },
      ],

      // Report by-worker table headers
      reportByWorkerHeaders: [
        { text: "Työntekijä", value: "workerName" },
        {
          text: "Henkilötyötunnit yht.",
          value: "totalPersonHours",
          width: 160,
        },
        { text: "Konetunnit yht.", value: "totalMachineHours", width: 140 },
        { text: "Merkintöjä", value: "entryCount", width: 110 },
        { text: "", value: "data-table-expand", sortable: false },
      ],
    };
  },

  computed: {
    henkilotItems() {
      return (this.$store.state.henkilot || []).map((h) => ({
        id: h.id,
        name: h.name,
        _isHenkilo: true,
      }));
    },

    activeWorkTasks() {
      return this.workTasks.filter((t) => t.active);
    },

    activeHours() {
      return this.hours.filter((h) => !h.deleted);
    },

    deletedHours() {
      return this.deletedHoursList;
    },

    taskHourSummary() {
      return this.buildTaskSummary(this.activeHours);
    },

    reportYearHours() {
      return this.activeHours.filter((h) => h.year === this.reportYear);
    },

    reportTaskSummary() {
      return this.buildTaskSummary(this.reportYearHours);
    },

    reportByTask() {
      const taskMap = {};
      for (const task of this.workTasks) {
        taskMap[task.id] = {
          taskId: task.id,
          title: task.title,
          minPersonHours: task.minPersonHours,
          totalPersonHours: 0,
          totalMachineHours: 0,
          workerNames: new Set(),
          entryCount: 0,
          entries: [],
        };
      }
      for (const h of this.reportYearHours) {
        if (!taskMap[h.taskId]) {
          taskMap[h.taskId] = {
            taskId: h.taskId,
            title: h.taskTitle,
            minPersonHours: null,
            totalPersonHours: 0,
            totalMachineHours: 0,
            workerNames: new Set(),
            entryCount: 0,
            entries: [],
          };
        }
        const t = taskMap[h.taskId];
        t.totalPersonHours += h.personHours || 0;
        t.totalMachineHours += h.machineHours || 0;
        t.entryCount += 1;
        if (h.worker) t.workerNames.add(h.worker.name);
        t.entries.push(h);
      }
      return orderBy(
        Object.values(taskMap)
          .filter((t) => t.entryCount > 0)
          .map((t) => {
            const totalPersonHours = this.roundHours(t.totalPersonHours);
            return {
              ...t,
              totalPersonHours,
              totalMachineHours: this.roundHours(t.totalMachineHours),
              workerCount: t.workerNames.size,
              entries: orderBy(t.entries, ["date"], ["asc"]),
              belowMin:
                t.minPersonHours != null && totalPersonHours < t.minPersonHours,
            };
          }),
        ["title"],
        ["asc"],
      );
    },

    reportByWorker() {
      const workerMap = {};
      for (const h of this.reportYearHours) {
        const name = h.worker ? h.worker.name : "Tuntematon";
        if (!workerMap[name]) {
          workerMap[name] = {
            workerName: name,
            totalPersonHours: 0,
            totalMachineHours: 0,
            entryCount: 0,
            entries: [],
          };
        }
        const w = workerMap[name];
        w.totalPersonHours += h.personHours || 0;
        w.totalMachineHours += h.machineHours || 0;
        w.entryCount += 1;
        w.entries.push(h);
      }
      return orderBy(
        Object.values(workerMap).map((w) => ({
          ...w,
          totalPersonHours: this.roundHours(w.totalPersonHours),
          totalMachineHours: this.roundHours(w.totalMachineHours),
          entries: orderBy(w.entries, ["date"], ["asc"]),
        })),
        ["workerName"],
        ["asc"],
      );
    },

    saveButtonText() {
      if (this.editingHour.id) return "Tallenna";
      const count = (this.editingHour.workers || []).length;
      return count > 1 ? `Tallenna (${count} merkintää)` : "Tallenna";
    },

    myselfAlreadyAdded() {
      const myName = this.$userDBContext.user;
      if (!myName) return true;
      return (this.editingHour.workers || []).some((w) => {
        const name = typeof w === "string" ? w : w.name;
        return name === myName;
      });
    },

    availableYears() {
      const years = new Set(this.hours.map((h) => h.year));
      years.add(dayjs().year());
      return orderBy([...years], [], ["desc"]);
    },

    canSaveHour() {
      const h = this.editingHour;
      const personHours = Number(h.personHours || 0);
      const machineHours = Number(h.machineHours || 0);
      if (!h.date || !h.taskId) return false;
      if (personHours < 0 || machineHours < 0) return false;
      if (personHours <= 0 && machineHours <= 0) return false;
      if (h.id) {
        // Editing: single worker must be set
        if (!h.singleWorker) return false;
      } else {
        // Creating: at least one worker
        if (!h.workers || h.workers.length === 0) return false;
      }
      return true;
    },

    hourDialogWarning() {
      const h = this.editingHour;
      if (!h.taskId) return null;
      const task = this.workTasks.find((t) => t.id === h.taskId);
      if (!task || task.minPersonHours == null) return null;

      const currentTotal = this.activeHours
        .filter((r) => r.taskId === h.taskId && r.id !== h.id)
        .reduce((sum, r) => sum + (r.personHours || 0), 0);

      const workerCount = h.id ? 1 : (h.workers || []).length;
      const newHours = (h.personHours || 0) * workerCount;
      const projected = currentTotal + newHours;

      if (projected > task.minPersonHours) {
        return `Huom: Tehtävän "${task.title}" henkilötyötuntien yhteismäärä (${projected} h) ylittää minimin (${task.minPersonHours} h).`;
      }
      return null;
    },
  },

  async created() {
    await Promise.all([
      this.$store.dispatch("loadHenkilot"),
      this.loadWorkTasks(),
      this.loadHours(),
    ]);
  },

  methods: {
    // --- Empty objects ---
    emptyTask() {
      return {
        id: null,
        title: "",
        minPersonHours: null,
        active: true,
        ajankohta: "",
      };
    },

    emptyHour() {
      const activeIds = new Set(this.activeWorkTasks.map((t) => t.id));
      const defaultTaskId =
        this.activeWorkTasks.length === 1
          ? this.activeWorkTasks[0].id
          : this.lastTaskId && activeIds.has(this.lastTaskId)
          ? this.lastTaskId
          : null;
      return {
        id: null,
        date: dayjs().format("YYYY-MM-DD"),
        taskId: defaultTaskId,
        description: "",
        personHours: null,
        machineHours: null,
        workers: [],
        singleWorker: null,
      };
    },

    // --- Firestore loaders ---
    async loadWorkTasks() {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "savusauna_work_tasks"),
            where("deleted", "==", false),
            orderByFirestore("ajankohta", "asc"),
          ),
        );
        this.workTasks = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error("loadWorkTasks error:", e);
      }
    },

    async loadHours() {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "savusauna_hours"),
            where("deleted", "==", false),
            orderByFirestore("date", "desc"),
          ),
        );
        this.hours = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error("loadHours error:", e);
      }
    },

    async loadDeletedHours() {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "savusauna_hours"),
            where("deleted", "==", true),
            orderByFirestore("date", "desc"),
          ),
        );
        this.deletedHoursList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
      } catch (e) {
        console.error("loadDeletedHours error:", e);
      }
    },

    // --- Task CRUD ---
    openTaskDialog(task = null) {
      if (task) {
        this.editingTask = { ...task };
      } else {
        this.editingTask = this.emptyTask();
      }
      this.taskDialog = true;
    },

    async saveTask() {
      try {
        const minPersonHours = this.normalizeOptionalNumber(
          this.editingTask.minPersonHours,
        );
        if (
          this.editingTask.minPersonHours !== null &&
          this.editingTask.minPersonHours !== "" &&
          minPersonHours === null
        ) {
          this.$toast(
            "Minimituntimäärän pitää olla nolla tai positiivinen numero",
            { type: "error" },
          );
          return;
        }
        if (this.editingTask.id) {
          await updateDoc(
            doc(db, "savusauna_work_tasks", this.editingTask.id),
            {
              title: this.editingTask.title,
              minPersonHours,
              active: this.editingTask.active,
              ajankohta: this.editingTask.ajankohta || "",
              updatedAt: Date.now(),
              updatedBy: this.$userDBContext,
            },
          );
        } else {
          await addDoc(collection(db, "savusauna_work_tasks"), {
            title: this.editingTask.title,
            minPersonHours,
            active: this.editingTask.active,
            ajankohta: this.editingTask.ajankohta || "",
            createdAt: Date.now(),
            createdBy: this.$userDBContext,
            updatedAt: null,
            updatedBy: null,
            deleted: false,
            deletedAt: null,
            deletedBy: null,
          });
        }
        this.taskDialog = false;
        this.$toast("Tallennettu", { type: "success" });
        await this.loadWorkTasks();
      } catch (e) {
        console.error("saveTask error:", e);
        this.$toast("Virhe tallennuksessa", { type: "error" });
      }
    },

    async deactivateTask(task) {
      try {
        await updateDoc(doc(db, "savusauna_work_tasks", task.id), {
          active: !task.active,
          updatedAt: Date.now(),
          updatedBy: this.$userDBContext,
        });
        this.$toast(task.active ? "Tehtävä piilotettu" : "Tehtävä aktivoitu", {
          type: "success",
        });
        await this.loadWorkTasks();
      } catch (e) {
        console.error("deactivateTask error:", e);
        this.$toast("Virhe", { type: "error" });
      }
    },

    // --- Hour CRUD ---
    canEditItem(item) {
      return (
        (item.createdBy && item.createdBy.sub === this.$userDBContext.sub) ||
        this.$isSuperAdmin
      );
    },

    isTaskBelowMin(taskId) {
      const s = this.taskHourSummary.find((t) => t.taskId === taskId);
      return s ? s.belowMin : false;
    },

    openHourDialog(hour = null) {
      if (hour) {
        this.editingHour = {
          id: hour.id,
          date: hour.date,
          taskId: hour.taskId,
          description: hour.description || "",
          personHours: hour.personHours || 0,
          machineHours: hour.machineHours || 0,
          workers: [],
          singleWorker: hour.worker
            ? this.henkilotItems.find(
                (h) => h.id === hour.worker.henkiloId,
              ) || { ...hour.worker }
            : null,
        };
      } else {
        this.editingHour = this.emptyHour();
      }
      this.hourDialog = true;
    },

    resolveWorker(w) {
      if (typeof w === "string") {
        return { henkiloId: null, name: w, source: "freeform" };
      }
      if (w && w._isHenkilo) {
        return { henkiloId: w.id, name: w.name, source: "henkilot" };
      }
      // Fallback for other object shapes
      if (w && w.name) {
        return {
          henkiloId: w.henkiloId || w.id || null,
          name: w.name,
          source: w.source || "henkilot",
        };
      }
      return { henkiloId: null, name: String(w), source: "freeform" };
    },

    normalizeOptionalNumber(value) {
      if (value === null || value === undefined || value === "") return null;
      const numberValue = Number(value);
      if (Number.isNaN(numberValue) || numberValue < 0) return null;
      return numberValue;
    },

    roundHours(value) {
      return Math.round((Number(value) || 0) * 10) / 10;
    },

    sanitizeReportCell(value) {
      return String(value == null ? "" : value)
        .replace(/[\t\r\n]+/g, " ")
        .trim();
    },

    async saveHour() {
      this.saving = true;
      try {
        const h = this.editingHour;
        const task = this.workTasks.find((t) => t.id === h.taskId);
        const taskTitle = task ? task.title : "";

        if (h.id) {
          // Update existing
          const worker = this.resolveWorker(h.singleWorker);
          await updateDoc(doc(db, "savusauna_hours", h.id), {
            date: h.date,
            year: parseInt(h.date.substring(0, 4)),
            taskId: h.taskId,
            taskTitle,
            description: h.description || "",
            personHours: h.personHours || 0,
            machineHours: h.machineHours || 0,
            worker,
            updatedAt: Date.now(),
            updatedBy: this.$userDBContext,
            auditLog: arrayUnion({
              action: "updated",
              at: Date.now(),
              by: this.$userDBContext,
            }),
          });
          this.$toast("Päivitetty", { type: "success" });
        } else {
          // Create new — one doc per worker
          const workers = (h.workers || []).map((w) => this.resolveWorker(w));
          const batch = writeBatch(db);
          const createdAt = Date.now();
          for (const worker of workers) {
            const hourRef = doc(collection(db, "savusauna_hours"));
            batch.set(hourRef, {
              date: h.date,
              year: parseInt(h.date.substring(0, 4)),
              taskId: h.taskId,
              taskTitle,
              description: h.description || "",
              personHours: h.personHours || 0,
              machineHours: h.machineHours || 0,
              worker,
              createdAt,
              createdBy: this.$userDBContext,
              updatedAt: null,
              updatedBy: null,
              deleted: false,
              deletedAt: null,
              deletedBy: null,
              restoredAt: null,
              restoredBy: null,
              auditLog: [
                { action: "created", at: createdAt, by: this.$userDBContext },
              ],
            });
          }
          await batch.commit();
          this.$toast("Tallennettu", { type: "success" });
        }

        this.lastTaskId = this.editingHour.taskId;
        this.hourDialog = false;
        await this.loadHours();
      } catch (e) {
        console.error("saveHour error:", e);
        this.$toast("Virhe tallennuksessa", { type: "error" });
      } finally {
        this.saving = false;
      }
    },

    async saveAndAddAnother() {
      const prevDate = this.editingHour.date;
      const prevTaskId = this.editingHour.taskId;
      await this.saveHour();
      // saveHour closes the dialog on success — reopen with same date + task
      if (!this.hourDialog) {
        this.editingHour = this.emptyHour();
        this.editingHour.date = prevDate;
        this.editingHour.taskId = prevTaskId;
        this.hourDialog = true;
      }
    },

    addMyself() {
      const myName = this.$userDBContext.user;
      if (!myName) return;
      const match = this.henkilotItems.find((h) => h.name === myName);
      const entry = match || myName;
      this.editingHour.workers = [...(this.editingHour.workers || []), entry];
    },

    async deleteHour(item) {
      if (
        !(await this.$deleteConfirm(
          "Haluatko varmasti poistaa tuntimerkinnän?",
        ))
      )
        return;
      try {
        await updateDoc(doc(db, "savusauna_hours", item.id), {
          deleted: true,
          deletedAt: Date.now(),
          deletedBy: this.$userDBContext,
          auditLog: arrayUnion({
            action: "deleted",
            at: Date.now(),
            by: this.$userDBContext,
          }),
        });
        this.$toast("Poistettu", { type: "success" });
        await Promise.all([
          this.loadHours(),
          this.showDeleted ? this.loadDeletedHours() : Promise.resolve(),
        ]);
      } catch (e) {
        console.error("deleteHour error:", e);
        this.$toast("Virhe poistamisessa", { type: "error" });
      }
    },

    async restoreHour(item) {
      try {
        await updateDoc(doc(db, "savusauna_hours", item.id), {
          deleted: false,
          restoredAt: Date.now(),
          restoredBy: this.$userDBContext,
          auditLog: arrayUnion({
            action: "restored",
            at: Date.now(),
            by: this.$userDBContext,
          }),
        });
        this.$toast("Palautettu", { type: "success" });
        await Promise.all([this.loadHours(), this.loadDeletedHours()]);
      } catch (e) {
        console.error("restoreHour error:", e);
        this.$toast("Virhe palautuksessa", { type: "error" });
      }
    },

    // --- Summaries ---
    buildTaskSummary(hoursList) {
      const taskMap = {};
      for (const task of this.workTasks) {
        taskMap[task.id] = {
          taskId: task.id,
          title: task.title,
          minPersonHours: task.minPersonHours,
          totalPersonHours: 0,
          totalMachineHours: 0,
          belowMin: false,
        };
      }
      for (const h of hoursList) {
        if (taskMap[h.taskId]) {
          taskMap[h.taskId].totalPersonHours += h.personHours || 0;
          taskMap[h.taskId].totalMachineHours += h.machineHours || 0;
        }
      }
      const result = Object.values(taskMap);
      for (const s of result) {
        s.totalPersonHours = this.roundHours(s.totalPersonHours);
        s.totalMachineHours = this.roundHours(s.totalMachineHours);
        if (s.minPersonHours != null && s.totalPersonHours < s.minPersonHours) {
          s.belowMin = true;
        }
      }
      return orderBy(result, ["title"], ["asc"]);
    },

    // --- Report ---
    getReportRows() {
      const rows = this.reportYearHours.map((h) => ({
        date: dayjs(h.date).format("DD.MM.YYYY"),
        sortDate: h.date,
        personHours: this.roundHours(h.personHours),
        machineHours: this.roundHours(h.machineHours),
        taskDescription: this.sanitizeReportCell(
          h.taskTitle + (h.description ? ` - ${h.description}` : ""),
        ),
        workerName: this.sanitizeReportCell(h.worker ? h.worker.name : ""),
      }));
      return orderBy(rows, ["sortDate", "workerName"], ["asc", "asc"]);
    },

    async copyReport() {
      const rows = this.getReportRows();
      const header =
        "Päivämäärä\tHenkilö-työtunnit\tTraktori- ja konetyötunnit\tTyötehtävien kuvaus\t\tTyöntekjän nimi";
      const lines = rows.map(
        (r) =>
          `${r.date}\t${r.personHours}\t${r.machineHours}\t${r.taskDescription}\t\t${r.workerName}`,
      );
      const text = [header, ...lines].join("\n");
      try {
        await navigator.clipboard.writeText(text);
        this.$toast("Raportti kopioitu leikepöydälle", { type: "success" });
      } catch (e) {
        console.error("copyReport error:", e);
        this.$toast("Kopiointi epäonnistui", { type: "error" });
      }
    },

    downloadXlsx() {
      const rows = this.getReportRows();

      // Sheet 1: Leader report
      const leaderData = rows.map((r) => ({
        Päivämäärä: r.date,
        "Henkilö-työtunnit": r.personHours,
        "Traktori- ja konetyötunnit": r.machineHours,
        "Työtehtävien kuvaus": r.taskDescription,
        "": "",
        "Työntekjän nimi": r.workerName,
      }));

      // Sheet 2: Extended report
      const belowMinTaskIds = new Set(
        this.reportTaskSummary.filter((s) => s.belowMin).map((s) => s.taskId),
      );
      const extendedData = this.reportYearHours.map((h) => ({
        ID: h.id,
        Päivämäärä: h.date,
        Vuosi: h.year,
        "Tehtävä-ID": h.taskId,
        "Tehtävän nimi": h.taskTitle,
        Kuvaus: h.description || "",
        Henkilötyötunnit: h.personHours || 0,
        Konetunnit: h.machineHours || 0,
        "Työntekijän nimi": h.worker ? h.worker.name : "",
        HenkilöId: h.worker ? h.worker.henkiloId || "" : "",
        Lähde: h.worker ? h.worker.source : "",
        Luotu: h.createdAt ? new Date(h.createdAt).toLocaleString("fi-FI") : "",
        Luonut: h.createdBy ? h.createdBy.user : "",
        Päivitetty: h.updatedAt
          ? new Date(h.updatedAt).toLocaleString("fi-FI")
          : "",
        Päivittänyt: h.updatedBy ? h.updatedBy.user : "",
        Poistettu: h.deletedAt
          ? new Date(h.deletedAt).toLocaleString("fi-FI")
          : "",
        Poistanut: h.deletedBy ? h.deletedBy.user : "",
        Palautettu: h.restoredAt
          ? new Date(h.restoredAt).toLocaleString("fi-FI")
          : "",
        Palauttanut: h.restoredBy ? h.restoredBy.user : "",
        "Alle minimin": belowMinTaskIds.has(h.taskId) ? "Kyllä" : "Ei",
      }));

      const ws1 = XLSX.utils.json_to_sheet(leaderData);
      const ws2 = XLSX.utils.json_to_sheet(
        orderBy(
          extendedData,
          ["Päivämäärä", "Työntekijän nimi"],
          ["asc", "asc"],
        ),
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws1, "Leader-raportti");
      XLSX.utils.book_append_sheet(wb, ws2, "Laajennettu");
      XLSX.writeFile(wb, `Savusauna_raportti_${this.reportYear}.xlsx`, {
        compression: true,
      });
    },
  },

  watch: {
    showDeleted(val) {
      if (val) {
        this.loadDeletedHours();
      }
    },
  },
};
</script>

<style scoped></style>
